import React from "react";
import {
  Box,
  Grid,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useTheme,
  ButtonBase,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Example pedal icons – replace with your own
import DrivePedalIcon from "../../../static/images/TSDD/cabinets/cobalt_icon.png"
import ModPedalIcon from "../../../static/images/TSDD/cabinets/cobalt_icon.png";
import DelayPedalIcon from "../../../static/images/TSDD/cabinets/cobalt_icon.png";

// Example mode icons – you can use dedicated icons or reuse pedal art
import ModeAToneIcon from "../../../static/images/TSDD/cabinets/cobalt_icon.png";
import ModeBToneIcon from "../../../static/images/TSDD/cabinets/cobalt_icon.png";
import ModeCToneIcon from "../../../static/images/TSDD/cabinets/cobalt_icon.png";

import PedalDialog from "../../Utilities/PedalDialog";

// ---- Data --------------------------------------------------------------
import { PEDALS } from "./Data/PedalData";


// Shared chip style for “Drive / Modulation / …” bubbles
const tagChipSx = {
  borderRadius: 999,
  px: 1.4,
  height: 24,
  fontSize: "0.7rem",
  textTransform: "none",
  borderColor: "rgba(255,255,255,0.35)",
  bgcolor: "rgba(255,255,255,0.06)",
};
// ---- Main overview -----------------------------------------------------

const PedalsOverview = () => {
  const theme = useTheme();
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const handleOpen = (index) => {
    setSelectedIndex(index);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  const selectedPedal = selectedIndex !== null ? PEDALS[selectedIndex] : null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: theme.palette.grey[900],
        color: theme.palette.text.primary,
        display: "flex",
        flexDirection: "column",
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 4, lg: 6 },
      }}
    >
      {/* Section header */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography variant="overline" sx={{ opacity: 0.7 }}>
          Pedals
        </Typography>
        <Typography
          variant="h4"
          component="h2"
          sx={{ fontWeight: 700, mt: 1 }}
        >
          Drive, boost, sustain and movement. All in one chain.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            mt: 1.5,
            maxWidth: 640,
            mx: "auto",
            opacity: 0.85,
          }}
        >
          Stack overdrive, modulation compression and EQ pedals that were voiced to sit
          right with the cabinets and amps in the suite, so you can build a full
          sound without leaving the plugin.
        </Typography>
      </Box>

      {/* Pedal grid */}
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={3} justifyContent={"center"}>
          {PEDALS.map((pedal, index) => (
            <Grid item xs={12} sm={6} md={3} key={pedal.id}>
              <Card
                variant="outlined"
                sx={{
                  position: "relative",
                  borderRadius: 3,
                  height: "100%",
                  bgcolor: "rgba(24, 24, 28, 0.98)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid rgba(255,255,255,0.06)",
                  boxShadow:
                    "0 18px 45px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,255,255,0.02)",
                  transition:
                    "transform 0.22s ease-out, box-shadow 0.22s ease-out, border-color 0.22s ease-out, background 0.22s ease-out",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    background:
                      "radial-gradient(circle at 0% 0%, rgba(255,255,255,0.18), transparent 60%)",
                    opacity: 0.8,
                  },
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.9)",
                    boxShadow:
                      "0 26px 70px rgba(0,0,0,0.95), 0 0 0 1px rgba(255,255,255,0.55)",
                    transform: "translateY(-6px) scale(1.015)",
                    background:
                      "linear-gradient(145deg, rgba(28,28,32,1), rgba(38,38,44,1))",
                  },
                }}
              >
                <CardActionArea
                  onClick={() => handleOpen(index)}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    position: "relative",
                    zIndex: 1,
                  }}
                >
                  {/* Pedal artwork */}
                  <CardMedia
                    component="img"
                    image={pedal.image}
                    alt={pedal.name}
                    sx={{
                      aspectRatio: "4 / 3",
                      objectFit: "contain",
                      width: "100%",
                      background:
                        "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.06), transparent 55%)",
                    }}
                  />

                  {/* Text content */}
                  <CardContent sx={{ flexGrow: 1, width: "100%" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.7,
                        mb: 0.3,
                        textTransform: "uppercase",
                        letterSpacing: 1,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Pedal
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.95rem",
                      }}
                    >
                      {pedal.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.875rem !important",
                        mt: 0.5,
                        mb: 1,
                        opacity: 0.85,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {pedal.subtitle}
                    </Typography>

                    {/* Tag chips on the tiles – pill-shaped bubbles */}
                    {pedal.tags && pedal.tags.length > 0 && (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        flexWrap="wrap"
                        sx={{ mb: 1 }}
                      >
                        {pedal.tags.map((tag) => (
                          <Chip
                            key={tag}
                            size="small"
                            label={tag}
                            variant="outlined"
                            sx={tagChipSx}
                          />
                        ))}
                      </Stack>
                    )}

                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        opacity: 0.7,
                        color: theme.palette.text.secondary,
                      }}
                    >
                      Click to explore modes
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Popup */}
      <PedalDialog
        open={dialogOpen}
        pedal={selectedPedal}
        onClose={handleClose}
      />
    </Box>
  );
};

export default PedalsOverview;
